import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleYesterdayActuals,
  type HuddleYesterdayActualsResult,
  getHuddleYesterdayActualsOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleBreakdownToTableRows,
  mapHuddleSummaryToMetrics,
  type HuddleYesterdayActualsTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleYesterdayActualsOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleYesterdayActualsResult = {
  data: HuddleYesterdayActualsResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  breakdownRows: HuddleYesterdayActualsTableRow[]
}

function useHuddleYesterdayActualsFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleYesterdayActualsOptions & {
  fetcher: HuddleDateFetcher<HuddleYesterdayActualsResult>
}): UseHuddleYesterdayActualsResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load yesterday actuals',
  })

  const summaryMetrics = useMemo(
    () => mapHuddleSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const breakdownRows = useMemo(
    () => mapHuddleBreakdownToTableRows(data?.summaryBreakdown),
    [data?.summaryBreakdown],
  )

  return { data, loading, error, summaryMetrics, breakdownRows }
}

export function useHuddleYesterdayActuals(
  options: UseHuddleYesterdayActualsOptions,
): UseHuddleYesterdayActualsResult {
  return useHuddleYesterdayActualsFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayActuals,
  })
}
export function useHuddleYesterdayActualsOverview(
  options: UseHuddleYesterdayActualsOptions,
): UseHuddleYesterdayActualsResult {
  return useHuddleYesterdayActualsFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayActualsOverview,
  })
}
